import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import ContactActions from "../ui/ContactActions.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import { addressLine } from "../ui/contactLinks";
import { phoneUi } from "../ui/phoneUi";
import {
  CONTACT_ROLES,
  addressForm,
  contactForm,
  filterCustomers,
  loadAddresses,
  loadContacts,
  loadCustomer,
  loadCustomers,
  loadTaxCertificates,
  saveAddress,
  saveContact,
  taxCertificateLabel,
  taxRateDisplay,
} from "../../features/customers/customerData";
import "../mobile.css";

// The phone Customers page (plan Phase 2): search; one customer (?id=N, or the desktop's
// /customers/:customerId link) with its main contact and
// billing address, tax-certificate status (read only), contacts (call / text / email) and ship-to
// addresses (map); add or edit a contact or a ship-to. The customer's own details, deleting a ship-to and
// tax-certificate actions stay on the desktop. Layout only: features/customers/customerData.js is the
// desktop page's own code.

function customerId(searchParams, routeId) {
  const raw = searchParams.get("id") || routeId;
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

export default function CustomersScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { customerId: routeId } = useParams();
  const fromList = useRef(false);
  const id = customerId(searchParams, routeId);
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else if (routeId) navigate("/customers", { replace: true }); else setSearchParams({}, { replace: true });
  };
  if (id) return <CustomerDetail key={id} customerId={id} onAll={toList} />;
  return <CustomerList searchTerm={searchTerm} setSearchTerm={setSearchTerm} onOpen={(cid) => { fromList.current = true; setSearchParams({ id: String(cid) }); }} />;
}

function CustomerList({ searchTerm, setSearchTerm, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    loadCustomers()
      .then((data) => { if (live) { setRows(data); setError(null); } })
      .catch((err) => { if (live) setError(err.message || "Couldn't reach JobQ."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  const shown = useMemo(() => filterCustomers(rows, searchTerm), [rows, searchTerm]);
  return (
    <div className="jq-screen" data-testid="phone-customers">
      <CardList
        items={shown}
        getKey={(c) => c.customer_id}
        onItemClick={(c) => onOpen(c.customer_id)}
        renderCard={(c) => (
          <div data-testid={`cust-card-${c.customer_id}`}>
            <div className="jq-qcard-top">
              <span className="jq-qcard-title mb-0">{c.name}</span>
              {c.short_code && <span className="jq-tag">{c.short_code}</span>}
            </div>
            <div className="jq-qcard-meta">{[c.city, c.state].filter(Boolean).join(", ")}{c.contact_name ? ` · ${c.contact_name}` : ""}</div>
          </div>
        )}
        loading={loading}
        error={error}
        empty={rows.length ? "No customers match." : "No customers."}
        testId="cust-list"
        search={{ value: searchTerm, onChange: setSearchTerm, placeholder: "Name or main contact" }}
      />
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

function ContactPanel({ open, customerId: cid, contact, onClose, onSaved }) {
  const [form, setForm] = useState(contactForm(null));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(contactForm(contact)); }, [open, contact]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    const r = await saveContact({ customerId: cid, contactId: contact ? contact.contact_id : null, form, ui: phoneUi, setBusy });
    if (r.done) onSaved();
  };
  return (
    <FullScreenPanel
      open={open}
      title={contact ? "Edit contact" : "Add a contact"}
      onClose={busy ? undefined : onClose}
      testId="cust-contact-panel"
      footer={<button type="button" className="btn btn-primary btn-lg w-100" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save contact"}</button>}
    >
      <Field id="ct-name" label="Name"><input id="ct-name" className="form-control" value={form.name} onChange={set("name")} autoComplete="off" /></Field>
      <Field id="ct-role" label="Role">
        <select id="ct-role" className="form-select" value={form.role} onChange={set("role")}>
          {CONTACT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      <Field id="ct-email" label="Email"><input id="ct-email" type="email" inputMode="email" className="form-control" value={form.email} onChange={set("email")} autoComplete="off" /></Field>
      <Field id="ct-phone" label="Phone"><input id="ct-phone" type="tel" inputMode="tel" className="form-control" value={form.phone} onChange={set("phone")} autoComplete="off" /></Field>
    </FullScreenPanel>
  );
}

function AddressPanel({ open, customerId: cid, address, isFirst, onClose, onSaved }) {
  const [form, setForm] = useState(addressForm(null));
  const [busy, setBusy] = useState(false);
  // The form is filled when the panel opens; a list that lands later never wipes what was typed.
  const isFirstRef = useRef(isFirst);
  isFirstRef.current = isFirst;
  useEffect(() => { if (open) setForm(addressForm(address, { isFirst: isFirstRef.current })); }, [open, address]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    const r = await saveAddress({ customerId: cid, addressId: address ? address.address_id : null, form, ui: phoneUi, setBusy });
    if (r.done) onSaved();
  };
  const text = (k, label, props = {}) => (
    <Field id={`ad-${k}`} label={label}><input id={`ad-${k}`} className="form-control" value={form[k]} onChange={set(k)} autoComplete="off" {...props} /></Field>
  );
  return (
    <FullScreenPanel
      open={open}
      title={address ? "Edit ship-to address" : "Add a ship-to address"}
      onClose={busy ? undefined : onClose}
      testId="cust-address-panel"
      footer={<button type="button" className="btn btn-primary btn-lg w-100" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save address"}</button>}
    >
      {text("label", "Label (required)", { placeholder: "e.g. Appleton HQ" })}
      {text("address", "Street address")}
      {text("address_line2", "Suite / dock / line 2")}
      {text("city", "City")}
      {text("state", "State")}
      {text("zip_code", "Zip", { inputMode: "numeric" })}
      {text("contact_name", "Receiving contact")}
      {text("contact_phone", "Contact phone", { type: "tel", inputMode: "tel" })}
      {text("receiving_hours", "Receiving hours", { placeholder: "e.g. Mon–Fri 7:00 AM – 3:30 PM" })}
      <Field id="ad-notes" label="Delivery notes">
        <textarea id="ad-notes" className="form-control" rows={2} value={form.delivery_notes} onChange={set("delivery_notes")} placeholder="Gate code, call before delivery, special access…" />
      </Field>
      <label className="form-check">
        <input className="form-check-input" type="checkbox" checked={!!form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} />
        <span className="form-check-label">Default ship-to (picked first on new quotes and orders)</span>
      </label>
    </FullScreenPanel>
  );
}

function CustomerDetail({ customerId: cid, onAll }) {
  const [cust, setCust] = useState(null);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState(undefined); // undefined = loading; null = couldn't be read
  const [addresses, setAddresses] = useState(undefined);
  const [tax, setTax] = useState(null);
  const [contactEdit, setContactEdit] = useState(null); // {contact} | null
  const [addressEdit, setAddressEdit] = useState(null); // {address} | null

  // A failed re-read keeps the list it had (as the desktop does).
  const rereadContacts = async () => { const d = await loadContacts(cid); setContacts((prev) => d || prev); };
  const rereadAddresses = async () => { const d = await loadAddresses(cid); setAddresses((prev) => d || prev); };
  const closeContact = useCallback(() => setContactEdit(null), []);
  const closeAddress = useCallback(() => setAddressEdit(null), []);

  useEffect(() => {
    let live = true;
    loadCustomer(cid)
      .then((d) => { if (live) setCust(d); })
      .catch((err) => { if (live) setError(err.message); });
    loadContacts(cid).then((d) => { if (live) setContacts(d); });
    loadAddresses(cid).then((d) => { if (live) setAddresses(d); });
    loadTaxCertificates(cid).then((d) => { if (live) setTax(d); }).catch(() => { if (live) setTax({ failed: true }); });
    return () => { live = false; };
  }, [cid]);

  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All customers</button>;
  if (error && !cust) return <div className="jq-screen" data-testid="phone-customer">{back}<div className="jq-note jq-note-danger" role="alert">{error}</div></div>;
  if (!cust) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;

  const billing = addressLine(cust);
  const rate = taxRateDisplay(cust.tax_rate);
  return (
    <div className="jq-screen" data-testid="phone-customer">
      {back}
      <section className="jq-card" data-testid="cust-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{cust.name}</span>
          {cust.short_code && <span className="jq-tag">{cust.short_code}</span>}
        </div>
        {cust.contact_name && <div className="fw-semibold">{cust.contact_name}</div>}
        {billing && <div className="jq-muted">{billing}</div>}
        <ContactActions phone={cust.phone} email={cust.email} address={billing} testId="cust-actions" />
        <dl className="jq-kv mt-2 mb-0">
          <div><dt>Terms</dt><dd>{cust.payment_terms || "—"}</dd></div>
          <div><dt>Tax</dt><dd>{rate.exempt ? "Exempt" : `${parseFloat(Number(rate.percent).toFixed(4))}%`}</dd></div>
          <div>
            <dt>Tax certificate</dt>
            <dd data-testid="cust-tax">{tax ? (tax.failed ? "Couldn't read" : taxCertificateLabel(tax.certificate_status)) : "…"}</dd>
          </div>
          <div><dt>Ships by</dt><dd>{{ shipping: "Carrier", pickup: "Customer pickup", delivery: "We deliver" }[cust.default_fulfillment_type] || "—"}</dd></div>
        </dl>
      </section>

      <div className="d-flex align-items-center mt-3">
        <h2 className="jq-section-title flex-grow-1 mb-0">Contacts</h2>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setContactEdit({ contact: null })}>Add contact</button>
      </div>
      {contacts === undefined && <div className="jq-muted mt-2">Loading…</div>}
      {contacts === null && <div className="jq-muted mt-2">Couldn't read the contacts.</div>}
      {contacts && contacts.length === 0 && <div className="jq-list-state">No contacts yet.</div>}
      {(contacts || []).map((c) => (
        <div key={c.contact_id} className="jq-card" data-testid={`cust-contact-${c.contact_id}`}>
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semibold flex-grow-1">{c.name}</span>
            <span className="jq-tag">{c.role}</span>
          </div>
          {(c.email || c.phone) && <div className="jq-muted">{[c.email, c.phone].filter(Boolean).join(" · ")}</div>}
          <ContactActions phone={c.phone} email={c.email} testId={`cust-contact-actions-${c.contact_id}`} />
          <button type="button" className="btn btn-link btn-sm px-0" onClick={() => setContactEdit({ contact: c })}>Edit</button>
        </div>
      ))}

      <div className="d-flex align-items-center mt-3">
        <h2 className="jq-section-title flex-grow-1 mb-0">Ship-to addresses</h2>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setAddressEdit({ address: null })}>Add address</button>
      </div>
      {addresses === undefined && <div className="jq-muted mt-2">Loading…</div>}
      {addresses === null && <div className="jq-muted mt-2">Couldn't read the ship-to addresses.</div>}
      {addresses && addresses.length === 0 && <div className="jq-list-state">No ship-to addresses yet.</div>}
      {(addresses || []).map((a) => {
        const line = addressLine(a);
        return (
          <div key={a.address_id} className="jq-card" data-testid={`cust-address-${a.address_id}`}>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold flex-grow-1">{a.label}</span>
              {a.is_default && <span className="jq-tag jq-tag-next">Default</span>}
            </div>
            {line && <div className="jq-muted">{line}</div>}
            {(a.contact_name || a.contact_phone) && <div className="jq-muted">Receiving: {[a.contact_name, a.contact_phone].filter(Boolean).join(" · ")}</div>}
            {a.receiving_hours && <div className="jq-muted">Hours: {a.receiving_hours}</div>}
            {a.delivery_notes && <div className="jq-muted">{a.delivery_notes}</div>}
            <ContactActions phone={a.contact_phone} address={line} testId={`cust-address-actions-${a.address_id}`} />
            <button type="button" className="btn btn-link btn-sm px-0" onClick={() => setAddressEdit({ address: a })}>Edit</button>
          </div>
        );
      })}

      <ContactPanel
        open={!!contactEdit}
        customerId={cid}
        contact={contactEdit?.contact || null}
        onClose={closeContact}
        onSaved={() => { setContactEdit(null); rereadContacts(); }}
      />
      <AddressPanel
        open={!!addressEdit}
        customerId={cid}
        address={addressEdit?.address || null}
        isFirst={Array.isArray(addresses) && addresses.length === 0}
        onClose={closeAddress}
        onSaved={() => { setAddressEdit(null); rereadAddresses(); }}
      />
    </div>
  );
}
